import { MouseEventHandler, ReactNode } from 'react'

import clsx from 'clsx'

import { ChildProps } from '@/common/util/props'

import IconButton from '@/components/view/atoms/button/IconButton'
import { IconColor, IconType } from '@/components/view/atoms/icon/Icon'

export const RowPaddingType = {
  DEFAULT: 'default',
  NO: 'no',
  THICK: 'thick',
  THIN: 'thin',
  MIDDLE: 'middle',
  NAV: 'nav'
} as const

export type RowPaddingType = (typeof RowPaddingType)[keyof typeof RowPaddingType]

interface Props extends ChildProps {
  type?: RowPaddingType
  hierarchyChild?: boolean
  hierarchyLast?: boolean
  disabled?: boolean
  background?: 'primary' | 'secondary' | 'caution' | 'transparent'
  childNode?: ReactNode
  border?: boolean
  onClickAccordion?: MouseEventHandler<HTMLButtonElement>
  accordionOpening?: boolean
  accordionRightNode?: ReactNode
  lastRadius?: boolean
  lastNoBorder?: boolean
  onClickDelete?: MouseEventHandler<HTMLButtonElement>
}

const Row = ({
  children,
  type = RowPaddingType.DEFAULT,
  hierarchyChild = false,
  hierarchyLast = false,
  background = 'primary',
  childNode,
  border = false,
  onClickAccordion = undefined,
  accordionOpening = false,
  onClickDelete = undefined,
  accordionRightNode,
  disabled = false,
  lastRadius = false,
  lastNoBorder = false
}: Props) => {
  return (
    <li className={clsx('w-full list-none')}>
      <div
        className={clsx(
          'flex items-center justify-between',
          background == 'secondary'
            ? 'bg-surface-secondary'
            : background == 'caution'
              ? 'bg-surface-caution'
              : background == 'transparent'
                ? 'bg-surface-transparent'
                : 'bg-surface',
          hierarchyChild &&
            'before:bg-fill-tertiary after:bg-fill-tertiary relative pl-2 before:absolute before:inset-y-0 before:left-0 before:z-10 before:m-auto before:h-px before:w-2 after:absolute after:top-0 after:left-0 after:m-auto after:w-px',
          hierarchyChild && (hierarchyLast ? 'after:h-[50%]' : 'after:h-full'),
          type == RowPaddingType.NO
            ? 'p-0'
            : type == RowPaddingType.THICK
              ? 'px-4 py-4'
              : type == RowPaddingType.MIDDLE
                ? 'px-3 py-2'
                : type == RowPaddingType.THIN
                  ? 'gap-x-1 px-4 py-1'
                  : type == RowPaddingType.NAV
                    ? 'gap-x-3 py-1 pr-3 pl-[2px]'
                    : `gap-x-1 py-1 pr-3 pl-1`,
          lastNoBorder
            ? 'border-primary border-b last:border-none'
            : border
              ? 'border-primary border-b'
              : '',
          lastRadius && 'last:rounded-b-md'
        )}
      >
        {children}
        {onClickAccordion != undefined && (
          <div className='flex items-center gap-1'>
            <IconButton
              type={accordionOpening ? IconType.UP : IconType.DOWN}
              color={IconColor.SECONDARY}
              onClick={onClickAccordion}
              disabled={disabled}
            />
            {accordionRightNode && <>{accordionRightNode}</>}
          </div>
        )}
        {onClickDelete != undefined && (
          <div className='ml-3'>
            <IconButton
              type={IconType.DELETE}
              color={IconColor.SECONDARY}
              onClick={onClickDelete}
              line
              disabled={disabled}
            />
          </div>
        )}
      </div>
      {childNode && <>{childNode}</>}
    </li>
  )
}

export default Row
